import {UpcomingEvent} from 'api/events/events.types';

export const OPT_OUT_WIZARD_FLOW = {
    /** Opt-in flow */
    OPT_IN: 'opt-in',
    /** Opt-out flow */
    OPT_OUT: 'opt-out',
} as const;

export type OptOutWizardFlowType =
    (typeof OPT_OUT_WIZARD_FLOW)[keyof typeof OPT_OUT_WIZARD_FLOW];

export const OPT_OUT_WIZARD_STATE = {
    /** No overlapping events, no blackouts */
    OPT_IN_DEFAULT: 'OPT_IN_DEFAULT',
    /** Overlapping events, no blackouts */
    OPT_IN_OVERLAPPING_EVENTS: 'OPT_IN_OVERLAPPING_EVENTS',
    /** Overlapping events with blackouts */
    OPT_IN_OVERLAPPING_WITH_BLACKOUTS: 'OPT_IN_OVERLAPPING_WITH_BLACKOUTS',
    /** Blackouts only */
    OPT_IN_BLACKOUT_ONLY: 'OPT_IN_BLACKOUT_ONLY',
    OPT_OUT_DEFAULT: 'OPT_OUT_DEFAULT',
    OPT_OUT_RESERVATIONS: 'OPT_OUT_RESERVATIONS',
} as const;

type OptOutWizardStateType =
    (typeof OPT_OUT_WIZARD_STATE)[keyof typeof OPT_OUT_WIZARD_STATE];

/**
 * Determines whether the provided event has overlapping events.
 * @param data - UpcomingEvent object containing overlapping events.
 */
export const hasOverlappingEvents = (data: UpcomingEvent) =>
    Boolean(data.overlappingEvents.length);

/**
 * Determines whether the provided event has blackouts.
 * @param data - UpcomingEvent object containing blackouts.
 */
export const hasBlackouts = (data: UpcomingEvent) =>
    Boolean(data.blackouts.length);

/**
 * Checks if the provided event has reservations.
 * @param data - UpcomingEvent object containing current tier information.
 */
export const hasReservations = (data: UpcomingEvent) =>
    data.currentTier.currentTierNumber !== 1 ||
    data.currentTier.usedTierInventory > 0;

/**
 * Checks if the provided event has blackouts that can be deleted.
 * @param data - UpcomingEvent object containing blackouts.
 */
export const canDeleteBlackouts = (data: UpcomingEvent) =>
    data.blackouts.some((blackout) => blackout.canDelete);

/**
 * Factory function to determine the initial state for the opt-in wizard.
 * @param event - UpcomingEvent object containing event details.
 */
const optInWizardStateFactory = (
    event: UpcomingEvent
): OptOutWizardStateType => {
    if (!hasOverlappingEvents(event) && !hasBlackouts(event)) {
        return OPT_OUT_WIZARD_STATE.OPT_IN_DEFAULT;
    } else if (hasOverlappingEvents(event) && !hasBlackouts(event)) {
        return OPT_OUT_WIZARD_STATE.OPT_IN_OVERLAPPING_EVENTS;
    } else if (hasOverlappingEvents(event) && hasBlackouts(event)) {
        return OPT_OUT_WIZARD_STATE.OPT_IN_OVERLAPPING_WITH_BLACKOUTS;
    } else {
        return OPT_OUT_WIZARD_STATE.OPT_IN_BLACKOUT_ONLY;
    }
};

/**
 * Factory function to determine the initial state for the opt-out wizard.
 * @param event - UpcomingEvent object containing event details.
 */
const optOutWizardStateFactory = (
    event: UpcomingEvent
): OptOutWizardStateType => {
    if (hasReservations(event)) {
        return OPT_OUT_WIZARD_STATE.OPT_OUT_RESERVATIONS;
    }

    return OPT_OUT_WIZARD_STATE.OPT_OUT_DEFAULT;
};

/**
 * Conditionally determines the initial state of the opt-out wizard.
 * @param event - UpcomingEvent object containing event details.
 * @param flow - The flow type, either opt-in or opt-out.
 */
export const determineInitialState = (
    event: UpcomingEvent,
    flow: OptOutWizardFlowType
): OptOutWizardStateType => {
    if (flow === OPT_OUT_WIZARD_FLOW.OPT_IN) {
        return optInWizardStateFactory(event);
    } else if (flow === OPT_OUT_WIZARD_FLOW.OPT_OUT) {
        return optOutWizardStateFactory(event);
    }

    throw new Error(`Unknown flow type: ${flow}`);
};

/**
 * Returns the steps for the opt-out wizard based on the current state. Steps are displayed in the order they are defined for each state instance.
 * @param currentState
 */
export const getWizardSteps = (
    currentState: OptOutWizardStateType
): string[] => {
    const stepsConfig: Record<OptOutWizardStateType, string[]> = {
        [OPT_OUT_WIZARD_STATE.OPT_IN_DEFAULT]: ['StartSellingDefault'],
        [OPT_OUT_WIZARD_STATE.OPT_IN_OVERLAPPING_EVENTS]: [
            'StartSellingDefault',
        ],
        [OPT_OUT_WIZARD_STATE.OPT_IN_OVERLAPPING_WITH_BLACKOUTS]: [
            'StartSellingRemoveBlackout',
            'StartSellingDefault',
        ],
        [OPT_OUT_WIZARD_STATE.OPT_IN_BLACKOUT_ONLY]: [
            'StartSellingRemoveBlackout',
            'StartSellingDefault',
        ],
        [OPT_OUT_WIZARD_STATE.OPT_OUT_DEFAULT]: ['StopSellingDefault'],
        [OPT_OUT_WIZARD_STATE.OPT_OUT_RESERVATIONS]: [
            'StopSellingDefault',
            'StopSellingExistingReservations',
        ],
    };

    return stepsConfig[currentState];
};
